import { z } from 'zod';
import { Email, Slug, Uuid } from './common.js';
/** Minimum length is a platform setting (auth.passwordMinLength, default 12); this is the hard floor/ceiling. */
export const Password = z.string().min(8).max(256);
/** Six-digit TOTP code. */
export const TotpCode = z.string().regex(/^\d{6}$/);
/** Recovery code, shown to the user as XXXXX-XXXXX. Dashes and case are ignored. */
export const RecoveryCode = z.string().trim().min(10).max(16);
/**
 * Where the login is up to. The session cookie is set at every stage, but only `complete`
 * sessions can call anything other than the auth endpoints for the next stage.
 */
export const AuthStage = z.enum([
    'mfa', // password ok, TOTP (or recovery code) needed
    'mfa_setup', // password ok, 2FA is required but not set up yet
    'choose_tenant', // account belongs to several tenants (or none active yet)
    'password_change', // Phase 4 (ADR 0028 / core-phase4 s2): the chosen tenant's password policy says the password has expired
    'complete',
]);
export const TenantChoice = z.object({
    tenantId: Uuid,
    name: z.string(),
    slug: Slug,
});
export const LoginRequest = z.object({
    email: Email,
    password: Password,
    /** Optional: preselect tenant when the portal is opened on <slug>.cloudsign.uk. */
    tenantSlug: Slug.optional(),
});
export const AuthStateResponse = z.object({
    stage: AuthStage,
    /** Present when stage is choose_tenant. */
    tenants: z.array(TenantChoice).optional(),
    /** True when the account is a platform admin (it may also choose "platform" instead of a tenant). */
    platformAdmin: z.boolean().optional(),
});
export const TotpVerifyRequest = z.object({ code: TotpCode });
export const RecoveryVerifyRequest = z.object({ code: RecoveryCode });
export const TotpSetupResponse = z.object({
    /** Base32 secret, shown for manual entry. */
    secret: z.string(),
    /** otpauth:// URL; the client renders it as a QR code. */
    otpauthUrl: z.string(),
});
export const TotpEnableRequest = z.object({ code: TotpCode });
export const RecoveryCodesResponse = z.object({
    /** Shown once. Ten codes. */
    codes: z.array(z.string()).length(10),
});
/**
 * POST /auth/totp/enable. Always returns the recovery codes (shown once). When the call was made during
 * login set-up (stage mfa_setup) the response also carries the next login stage, like the other
 * login-step responses; when made from a complete session `stage` is 'complete'.
 */
export const TotpEnableResponse = RecoveryCodesResponse.extend({
    stage: AuthStage,
    tenants: z.array(TenantChoice).optional(),
    platformAdmin: z.boolean().optional(),
});
/**
 * GET /api/v1/auth/session (public access). Without a valid session cookie: 200 with `{ stage: null }`
 * (never 401, so the portal can call it on every page load). With one: the current stage and area.
 */
export const SessionStateResponse = z.object({
    stage: AuthStage.nullable(),
    /** 'building' (ADR 0034, C-SR1): a building admin or marshal in a shared reception's building area. */
    area: z.enum(['tenant', 'platform', 'building']).nullable(),
    tenants: z.array(TenantChoice).optional(),
    platformAdmin: z.boolean().optional(),
    /** Shared reception (ADR 0034, C-SR1): buildings this account can enter (area picker). Absent = none. */
    buildings: z.array(z.object({ buildingId: Uuid, name: z.string(), role: z.enum(['building_admin', 'building_marshal']) })).optional(),
});
export const TotpDisableRequest = z.object({ password: Password });
export const SelectTenantRequest = z.union([
    z.object({ tenantId: Uuid }),
    /** Platform admins only: enter the platform area. */
    z.object({ platform: z.literal(true) }),
]);
export const ChangePasswordRequest = z.object({
    currentPassword: Password,
    newPassword: Password,
});
export const ForgotPasswordRequest = z.object({ email: Email });
export const ResetPasswordRequest = z.object({
    token: z.string().min(20).max(200),
    newPassword: Password,
});
export const InviteInfoResponse = z.object({
    tenantName: z.string().nullable(),
    /** Masked, e.g. j***@example.com. */
    emailMasked: z.string(),
    /** False when the account already has a password (just accept to join). */
    needsPassword: z.boolean(),
    fullName: z.string().nullable(),
    /** The platform's minimum password length, so the page can show the rule before the person types. */
    passwordMinLength: z.number().int().min(8),
});
export const AcceptInviteRequest = z.object({
    token: z.string().min(20).max(200),
    password: Password.optional(),
    fullName: z.string().trim().min(1).max(120).optional(),
});
//# sourceMappingURL=auth.js.map